import { useEffect, useRef } from 'react';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard basics for the 245D documents dialogs that are not built on the
 * shared Modal: focus moves into the dialog when it opens, Escape closes it
 * (unless `canClose()` says not now, e.g. while a request is in flight), and
 * focus goes back to whatever opened it when it closes.
 *
 * @param {import('react').RefObject<HTMLElement>} panelRef  The dialog panel.
 * @param {{ onClose: Function, canClose?: () => boolean }} options
 */
export default function useDialogFocus(panelRef, { onClose, canClose = () => true }) {
  const latest = useRef({ onClose, canClose });
  latest.current = { onClose, canClose };

  useEffect(() => {
    const opener = document.activeElement;
    const panel = panelRef.current;
    const first = panel?.querySelector(FOCUSABLE);
    (first || panel)?.focus?.({ preventScroll: true });

    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || !latest.current.canClose()) return;
      event.stopPropagation();
      latest.current.onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && opener !== document.body && opener.isConnected) opener.focus?.({ preventScroll: true });
    };
  }, [panelRef]);
}
